/* /dashboard — port of prototype pgDashboard (role-aware). */
import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAiFeatures, useAllEmployees, useApprovals, useExpiringDocuments, useLeaves, usePayrollHistory, usePayrollRun, useToday } from "../../data/hooks";
import type { Employee } from "../../data/types";
import { BANDS, bandOf, nitaqat } from "../../domain/compliance";
import { daysBetween, yearsBetween } from "../../domain/dates";
import { useI18n, LOOKUP } from "../../i18n";
import { fmtN, gDate, MONTHS_AR, MONTHS_EN, moneyText, periodLabel } from "../../lib/format";
import { useSession } from "../../app/session";
import { Alert, Bar, Button, Card, Chip, Donut, DualDate, ExpiryChip, Gauge, HBars, Icon, IconButton, Kpi, LineChart, Person, PageHeader, Skeleton, ErrorBox, useToast } from "../../ui";
import { canWriteHr, withEmployees } from "./components/shared";
import "./core.css";
import { curL, useCountry } from "../../lib/country";

const SETUP_KEY = "rukn-setup-hidden";
function loadHidden(): boolean { try { return localStorage.getItem(SETUP_KEY) === "1"; } catch { return false; } }
function saveHidden() { try { localStorage.setItem(SETUP_KEY, "1"); } catch { /* ignore */ } }

/** Days until the next occurrence of a yyyy-mm-dd anniversary (0 = today). */
export function daysUntilAnniversary(date: string, today: string): number {
  const y = Number(today.slice(0, 4));
  for (const yy of [y, y + 1]) {
    let md = date.slice(5);
    if (md === "02-29" && !(yy % 4 === 0 && (yy % 100 !== 0 || yy % 400 === 0))) md = "02-28";
    const d = daysBetween(today, yy + "-" + md);
    if (d >= 0) return d;
  }
  return 365;
}

function GettingStarted({ hc, paid }: { hc: number; paid: boolean }) {
  const { L } = useI18n();
  const [hidden, setHidden] = useState(loadHidden);
  const items: [string, string, string, boolean][] = [
    ["company", L("Add company details", "أضف بيانات المنشأة"), "/settings", true],
    ["employees", L("Import your employees", "استورد الموظفين"), "/employees", hc > 0],
    ["gosi", L("Check GOSI & allowance settings", "راجع إعدادات التأمينات والبدلات"), "/settings", false],
    ["payroll", L("Run your first payroll", "شغّل أول مسير رواتب"), "/payroll/run", paid],
    ["invite", L("Invite employees to the app", "ادعُ الموظفين للتطبيق"), "/employees", false],
    ["subscribe", L("Choose a plan before the trial ends", "اختر باقة قبل انتهاء التجربة"), "/billing", false],
  ];
  const done = items.filter((x) => x[3]).length;
  if (hidden || done === items.length) return null;
  return (
    <section className="card gs mb" data-testid="getting-started">
      <div className="card-h">
        <div><h3>{L("Getting started", "البدء")}</h3><p>{L(done + " of " + items.length + " done · about 15 minutes left", "أُنجز " + done + " من " + items.length)}</p></div>
        <div className="row"><IconButton icon="x" label={L("Hide", "إخفاء")} onClick={() => { saveHidden(); setHidden(true); }} data-testid="gs-hide" /></div>
      </div>
      <div className="card-b">
        <div className="mb"><Bar value={done} max={items.length} /></div>
        <div className="gs-list">
          {items.map(([k, label, to, d]) => (
            <Link key={k} className={"gs-item" + (d ? " done" : "")} to={to}>
              <span className="gs-c">{d && <Icon name="check" className="s" />}</span><span>{label}</span>{!d && <Icon name="chevron-right" className="s" />}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ListRow({ children }: { children: ReactNode }) {
  return <div className="list-row">{children}</div>;
}

export default function Dashboard() {
  const i = useI18n();
  const { t, L, isAr, lang } = i;
  const country = useCountry();
  const { user, role } = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const today = useToday();
  const hr = canWriteHr(role);

  const empsQ = useAllEmployees();
  const runQ = usePayrollRun();
  const histQ = usePayrollHistory();
  const apprQ = useApprovals();
  const ex30Q = useExpiringDocuments(30);
  const aiQ = useAiFeatures();
  const leavesQ = useLeaves({ enabled: hr });

  const err = empsQ.error || runQ.error || histQ.error;
  if (err) return <ErrorBox error={err} onRetry={() => { empsQ.refetch(); runQ.refetch(); histQ.refetch(); }} />;
  if (!empsQ.data || !runQ.data || !histQ.data) return <Skeleton />;

  const emps = empsQ.data;
  const act = emps.filter((e) => e.status !== "Terminated");
  const nq = nitaqat(emps);
  const band = bandOf(nq.pct);
  // outside Saudi Arabia the share is simply the country's citizens (no Nitaqat bands)
  const isSA = country.code === "SA";
  const sa = act.filter((e) => e.nationality === country.code).length;
  const locPct = isSA ? nq.pct : act.length ? (sa / act.length) * 100 : 0;
  const ns = act.length - sa;
  const hist = histQ.data;
  const last = hist[hist.length - 1];
  const runTot = runQ.data.totals.gross;
  const delta = last && last.gross ? ((runTot - last.gross) / last.gross) * 100 : 0;
  const hcDelta = last ? act.length - last.employees : 0;
  const period = runQ.data.run.period;
  const pm = Number(period.slice(5, 7));
  const mLabel = (m: number) => L(MONTHS_EN[m - 1], MONTHS_AR[m - 1].slice(0, 3));
  const trend: [string, number][] = [...hist.slice(1).map((p) => [mLabel(p.month), p.gross] as [string, number]), [mLabel(pm), Math.round(runTot)]];
  const depts: [string, number][] = Object.keys(LOOKUP.DEPT).map((k) => [i.dept(k), act.filter((e) => e.dept === k).length] as [string, number]).sort((a, b) => b[1] - a[1]);
  const natc: Record<string, number> = {};
  for (const e of act) natc[e.nationality] = (natc[e.nationality] || 0) + 1;
  const top = Object.entries(natc).filter(([k]) => k !== "SA").sort((a, b) => b[1] - a[1]).slice(0, 3);
  const natParts: [string, number, string][] = [
    [i.nat("SA"), natc.SA || 0, "var(--chart-1)"],
    ...top.map(([k, n], j) => [i.nat(k), n, "var(--chart-" + (j + 2) + ")"] as [string, number, string]),
    [L("Other", "أخرى"), act.length - (natc.SA || 0) - top.reduce((a, x) => a + x[1], 0), "var(--chart-5)"],
  ];
  const ex30 = withEmployees(ex30Q.data ?? [], emps);
  const overdue = ex30.filter((x) => x.d.daysLeft < 0).length;
  const approvals = apprQ.data?.length ?? 0;
  const byId = new Map(emps.map((e) => [e.id, e] as [number, Employee]));
  const onLeave = (leavesQ.data ?? []).filter((l) => l.status === "Approved" && l.from <= today && l.to >= today);
  const bdays = act.map((e) => ({ e, n: daysUntilAnniversary(e.dob, today) })).sort((a, b) => a.n - b.n || a.e.id - b.e.id).slice(0, 2);
  const anniv = act.filter((e) => e.joined.slice(5, 7) === today.slice(5, 7) && e.joined.slice(0, 4) < today.slice(0, 4)).slice(0, 3);
  const aiHours = aiQ.data ? aiQ.data.features.reduce((a, f) => a + f.saveMinutes * f.uses, 0) / 60 : null;
  const run = runQ.data.run;
  const firstName = user ? (isAr ? user.nameAr : user.name).split(" ")[0] : "";
  const relDay = (n: number) => (n === 0 ? t("Today") : n === 1 ? L("Tomorrow", "غدًا") : L("in " + n + " days", "خلال " + n + " يومًا"));

  const kpis = (
    <div className="g g-kpi mb" data-testid="dash-kpis">
      <Kpi testId="kpi-headcount" label={t("Headcount")} value={String(act.length)} icon="users" to="/employees" foot={<><span className={hcDelta >= 0 ? "up" : "down"}>{hcDelta >= 0 ? "▲ " : "▼ "}{Math.abs(hcDelta)}</span> {t("vs last month")}</>} />
      <Kpi testId="kpi-payroll" label={t("Payroll cost")} value={fmtN(runTot, 0)} unit={curL(L)} icon="wallet" tone="gold" to="/payroll" foot={<><span className={delta > 0 ? "up" : "down"}>{delta > 0 ? "▲ " : "▼ "}{Math.abs(delta).toFixed(1)}%</span> {L("Draft for " + periodLabel(period, "en").split(" ")[0], "مسودة " + periodLabel(period, "ar").split(" ")[0])}</>} />
      <Kpi testId="kpi-saudization" label={t("Saudization")} value={locPct.toFixed(1)} unit="%" icon="target" to={hr ? "/saudization" : undefined} foot={isSA ? <Chip status={band.key} tone={band.tone} nodot /> : <span data-testid="kpi-loc-count">{sa} / {act.length} {L(country.demonym[0], country.demonym[1])}</span>} />
      <Kpi testId="kpi-approvals" label={t("Pending approvals")} value={String(approvals)} icon="inbox" tone="warn" to="/approvals" foot={L("leave, expenses, loans, letters", "إجازات ومصروفات وسلف وخطابات")} />
      <Kpi testId="kpi-expiring" label={t("Expiring documents")} value={String(ex30.length)} icon="id-card" tone="bad" to={hr ? "/documents" : undefined} foot={t("within 30 days") + " · " + overdue + " " + L("overdue", "منتهية")} />
    </div>
  );

  const quick = (
    <Card title={t("Quick actions")} testId="quick-actions">
      <div className="qa">
        {hr && <button type="button" className="qa-btn" onClick={() => nav("/employees/new")} data-testid="qa-add-employee"><span className="kpi-ico"><Icon name="user-plus" /></span>{t("Add employee")}</button>}
        <button type="button" className="qa-btn" onClick={() => nav("/payroll/run")} data-testid="qa-run-payroll"><span className="kpi-ico gold"><Icon name="wallet" /></span>{t("Run payroll")}</button>
        {hr
          ? <button type="button" className="qa-btn" onClick={() => nav("/leave")} data-testid="qa-approve-leave"><span className="kpi-ico warn"><Icon name="check" /></span>{t("Approve leave")}</button>
          : <button type="button" className="qa-btn" onClick={() => nav("/approvals")} data-testid="qa-approvals"><span className="kpi-ico warn"><Icon name="inbox" /></span>{t("Pending approvals")}</button>}
      </div>
      <div className="divider" style={{ margin: "16px 0" }} />
      <div className="stack">
        {!run.paid && (
          <Alert tone="bad" testId="alert-wps"
            title={run.exported ? L(periodLabel(period, "en") + " WPS file not acknowledged in Mudad", "لم يُعتمد ملف حماية الأجور لـ" + periodLabel(period, "ar") + " في مدد") : L(periodLabel(period, "en") + " WPS file not exported yet", "لم يُصدَّر ملف حماية الأجور لـ" + periodLabel(period, "ar") + " بعد")}
            desc={L("Mudad · upload before the pay day", "مدد · الرفع قبل يوم صرف الرواتب")}
            action={<Button variant="sm" onClick={() => nav("/payroll")}>{t("Open")}</Button>} />
        )}
        {ex30.length > 0 && (
          <Alert tone="warn" testId="alert-docs"
            title={L(ex30.length + " documents expire within 30 days", ex30.length + " وثائق تنتهي خلال 30 يومًا")}
            desc={L("Iqamas, insurance and visas", "إقامات وتأمين وتأشيرات")}
            action={<Button variant="sm" onClick={() => nav(hr ? "/documents" : "/employees")}>{t("Open")}</Button>} />
        )}
      </div>
    </Card>
  );

  const main = (
    <div>
      {kpis}
      <div className="g g-2-1 mb">
        <Card title={t("Payroll trend")} sub={t("Last 12 months · SAR")} actions={<Button variant="ghost sm" end="chevron-right" onClick={() => nav("/payroll")}>{t("View all")}</Button>} testId="payroll-trend">
          <LineChart data={trend} fmt={(v) => Math.round(v / 1000) + "k"} tip={(v) => moneyText(v, lang, 0)} label={t("Payroll trend")} />
        </Card>
        {quick}
      </div>
      <div className="g g-3">
        <Card title={t("Headcount by department")} testId="dept-card"><HBars data={depts} /></Card>
        <Card title={t("Nationality split")} testId="nat-card">
          <div className="row nw" style={{ gap: 20, alignItems: "center", flexWrap: "wrap" }}>
            <Donut parts={natParts} center={String(act.length)} sub={t("Employees")} />
            <div className="stack" style={{ gap: 8, flex: 1, minWidth: 140 }}>
              {natParts.map((p) => <div key={p[0]} className="row between sm"><span><i style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: p[2], marginInlineEnd: 8 }} />{p[0]}</span><span className="num">{p[1]}</span></div>)}
            </div>
          </div>
        </Card>
        {isSA ? <Card title={t("Nitaqat band")} testId="nitaqat-card">
          <Gauge pct={nq.pct} bands={BANDS} />
          <div className="row between" style={{ marginTop: 4 }}><Chip status={band.key} tone={band.tone} /><span className="sm muted">{sa} {t("Saudi")} · {ns} {t("Non-Saudi")}</span></div>
          {hr && <div className="mt"><Button variant="block sm" icon="sparkle" onClick={() => nav("/saudization")} data-testid="btn-simulator">{t("Hiring simulator")}</Button></div>}
        </Card> : <Card title={L(...country.localisation.short)} testId="localisation-card">
          <div className="kpi-v"><span>{locPct.toFixed(1)}</span><span className="kpi-u">%</span></div>
          <Bar value={locPct} max={100} />
          <div className="row between" style={{ marginTop: 8 }}><span className="sm muted">{sa} {L(country.demonym[0], country.demonym[1])} · {ns} {L("expatriate", "وافد")}</span></div>
          <p className="xs muted" style={{ margin: "8px 0 0" }}>{L(...country.localisation.summary)}</p>
          {hr && <div className="mt"><Button variant="block sm" icon="target" onClick={() => nav("/saudization")} data-testid="btn-localisation">{L("Open " + country.localisation.short[0], "فتح " + country.localisation.short[1])}</Button></div>}
        </Card>}
      </div>
    </div>
  );

  const side = (
    <div className="stack lg">
      <Card title={L("AI saved you", "وفّر لك الذكاء")} testId="ai-saved">
        <div className="kpi-v">{aiHours == null ? "—" : aiHours.toFixed(1)}<span className="kpi-u">{L("hours this month (est.)", "ساعة هذا الشهر (تقديري)")}</span></div>
        <div className="xs muted" style={{ margin: "4px 0 10px" }}>{L("Iqama scans, receipts, overtime sheets and assistant answers", "قراءة الإقامات والفواتير وجداول العمل الإضافي وإجابات المساعد")}</div>
        <Button variant="sm block" icon="sparkle" onClick={() => nav("/ai")}>{L("See AI automation", "عرض الأتمتة الذكية")}</Button>
      </Card>
      {hr && (
        <Card title={t("On leave today")} sub={gDate(today, lang, { weekday: "long", day: "numeric", month: "long" })} testId="on-leave">
          {onLeave.length
            ? onLeave.map((l) => { const e = byId.get(l.employeeId); return e ? <ListRow key={l.id}><Person e={e} meta={t(l.type) + " · " + L("until ", "حتى ") + gDate(l.to, lang)} /></ListRow> : null; })
            : <p className="muted sm">{L("Everyone is in today.", "الجميع حاضر اليوم.")}</p>}
        </Card>
      )}
      <Card title={t("Birthdays")} testId="birthdays">
        {bdays.map(({ e, n }) => <ListRow key={e.id}><Person e={e} meta={relDay(n)} /><span className="kpi-ico gold" style={{ width: 32, height: 32 }}><Icon name="cake" /></span></ListRow>)}
      </Card>
      <Card title={t("Work anniversaries")} testId="anniversaries">
        {anniv.length
          ? anniv.map((e) => { const y = Math.round(yearsBetween(e.joined, today)); return <ListRow key={e.id}><Person e={e} meta={L(y + " years · ", y + " سنوات · ") + gDate(today.slice(0, 4) + e.joined.slice(4), lang)} /><span className="kpi-ico"><Icon name="award" /></span></ListRow>; })
          : <p className="muted sm">{L("No anniversaries this month.", "لا توجد ذكرى تعيين هذا الشهر.")}</p>}
      </Card>
      <Card title={t("Upcoming expiries")} actions={hr ? <IconButton icon="chevron-right" label={t("View all")} onClick={() => nav("/documents")} /> : undefined} testId="upcoming-expiries">
        {ex30.length
          ? ex30.slice(0, 4).map((x) => <ListRow key={x.e.id + x.d.kind}><Person e={x.e} meta={t(x.d.kind)} /><ExpiryChip date={x.d.expiry} today={today} /></ListRow>)
          : <p className="muted sm">{L("Nothing expires in the next 30 days.", "لا توجد وثائق تنتهي خلال 30 يومًا.")}</p>}
      </Card>
    </div>
  );

  return (
    <div data-testid="dashboard">
      <PageHeader
        title={t("Good morning") + L(", ", "، ") + firstName}
        sub={<><DualDate date={today} inline /> · {t("Here's what needs your attention today.")}</>}
        actions={<>
          <Button icon="download" onClick={() => toast(L("Dashboard exported to PDF", "تم تصدير لوحة التحكم"))} data-testid="btn-export">{t("Export")}</Button>
          <Button variant="pri" icon="wallet" onClick={() => nav("/payroll/run")} data-testid="btn-run-payroll">{t("Run payroll")}</Button>
        </>}
      />
      {hr && <GettingStarted hc={act.length} paid={hist.some((h) => h.status === "Paid")} />}
      <div className="with-side">{main}{side}</div>
    </div>
  );
}
